/** Shades of the paid amber for a stack's layers, bottom up, alternating
 *  mid, light, dark and light so neighbours never share a lightness, and
 *  any two at least 26 ΔE (CIE76) apart, so a legend swatch tells them
 *  apart; the bottom one is the nearest to the paid amber, so a month with
 *  one campaign keeps close to the panel's colour. #FCD34D and #F59E0B are
 *  below 3:1 on white, so the legend and the tooltip name every layer. */
export const LAYER_COLORS = ["#B45309", "#FCD34D", "#78350F", "#F59E0B"] as const;
/** "Other" is no campaign of its own, so it recedes: a warm grey. */
export const OTHER_COLOR = "#D6D3D1";
const MAX_NAMED = LAYER_COLORS.length;

export interface StackInput {
  key: string;
  name: string;
  /** "YYYY-MM-DD"; null reads the first daily row. */
  startDate: string | null;
  /** The measure per day, a day without a row left out. */
  daily: ReadonlyArray<{ date: string; value: number }>;
}

export interface StackLayer {
  key: string;
  name: string;
  color: string;
  /** The month's sum, through `throughDay` on a running total. */
  total: number;
  /** "Other" folds more than one campaign. */
  other: boolean;
}

export interface StackRow {
  day: number;
  /** By layer key; null on a day outside the layer's days, never 0. */
  values: Record<string, number | null>;
  /** The stack's top; null when no layer has the day. */
  total: number | null;
}

export interface CampaignStack {
  /** Bottom first: oldest campaign at the bottom, "Other" on top. */
  layers: StackLayer[];
  /** Day 1 to `lastDay`. */
  rows: StackRow[];
}

interface StackOptions {
  year: number;
  month: number;
  /** The chart's last day, which can pass the month's own. */
  lastDay: number;
  /** A running total by day instead of the day's own value. */
  cumulative?: boolean;
  /** A running total stops here, the source's settled day. */
  throughDay?: number;
}

/** The month's campaigns as layers of a stacked area: the largest four by
 *  total named, oldest at the bottom, the rest folded into "Other" on top.
 *  A day a layer has no row for is null, a gap, never 0 (DD-021). A running
 *  total starts on the layer's first row and, once its campaign ends, keeps
 *  its last value, since the month's total still holds those clicks. Null
 *  when no campaign has a row in the month. */
export const campaignStack = (
  inputs: ReadonlyArray<StackInput>,
  { year, month, lastDay, cumulative = false, throughDay = lastDay }: StackOptions,
): CampaignStack | null => {
  const prefix = `${year}-${String(month).padStart(2, "0")}-`;
  const lastCounted = cumulative ? throughDay : lastDay;
  const campaigns = inputs
    .map((input) => {
      const byDay = new Map<number, number>();
      input.daily.forEach((d) => {
        if (!d.date.startsWith(prefix)) return;
        const day = Number(d.date.slice(8, 10));
        if (day > lastCounted) return;
        byDay.set(day, (byDay.get(day) ?? 0) + d.value);
      });
      const firstDay = Math.min(...Array.from(byDay.keys()));
      const total = Array.from(byDay.values()).reduce((a, b) => a + b, 0);
      const start = input.startDate ?? `${prefix}${String(firstDay).padStart(2, "0")}`;
      return { ...input, byDay, firstDay, start, total };
    })
    .filter((c) => c.byDay.size > 0);
  if (campaigns.length === 0) return null;

  const ranked = [...campaigns].sort((a, b) => b.total - a.total || a.start.localeCompare(b.start));
  const named = ranked
    .slice(0, MAX_NAMED)
    .sort((a, b) => a.start.localeCompare(b.start) || a.name.localeCompare(b.name));
  const folded = ranked.slice(MAX_NAMED);

  const groups = [
    ...named.map((c, i) => ({
      layer: { key: c.key, name: c.name, color: LAYER_COLORS[i], total: c.total, other: false },
      members: [c],
    })),
    ...(folded.length > 0
      ? [
          {
            layer: {
              key: "__other",
              name: `Other · ${folded.length} campaign${folded.length === 1 ? "" : "s"}`,
              color: OTHER_COLOR,
              total: folded.reduce((sum, c) => sum + c.total, 0),
              other: true,
            },
            members: folded,
          },
        ]
      : []),
  ];

  const running = new Map<string, number>();
  const rows: StackRow[] = Array.from({ length: lastDay }, (_, i) => {
    const day = i + 1;
    const values: Record<string, number | null> = {};
    groups.forEach(({ layer, members }) => {
      const own = members.filter((m) => m.byDay.has(day));
      const sum = own.reduce((acc, m) => acc + (m.byDay.get(day) ?? 0), 0);
      if (!cumulative) {
        values[layer.key] = own.length > 0 ? sum : null;
        return;
      }
      const started = members.some((m) => m.firstDay <= day);
      const value = (running.get(layer.key) ?? 0) + sum;
      running.set(layer.key, value);
      values[layer.key] = started && day <= throughDay ? value : null;
    });
    const known = Object.values(values).filter((v): v is number => v != null);
    return { day, values, total: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null };
  });
  return { layers: groups.map((g) => g.layer), rows };
};

/** The rows a stacked chart draws: where a layer starts or stops between
 *  two days, both days repeat once with only the layers they share, so the
 *  layers above step on a vertical edge at the day instead of sloping
 *  across to the next one. The repeats keep their day, so a tooltip reads
 *  the day's own row; a layer missing a day stays null, never 0 (DD-021). */
export const stackEdges = (rows: ReadonlyArray<StackRow>): StackRow[] =>
  rows.flatMap((row, i) => {
    const next = rows[i + 1];
    const keys = Object.keys(row.values);
    const has = (r: StackRow, key: string) => r.values[key] != null;
    if (!next || keys.every((key) => has(row, key) === has(next, key))) return [row];
    const shared = (r: StackRow): StackRow => {
      const values = Object.fromEntries(
        keys.map((key) => [key, has(row, key) && has(next, key) ? r.values[key] : null]),
      );
      const known = Object.values(values).filter((v): v is number => v != null);
      return { day: r.day, values, total: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null };
    };
    return [row, shared(row), shared(next)];
  });
